import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import cortesPdfService from '../services/cortesPdfService';
import LoadingSpinner from './LoadingSpinner';
import ConfirmModal from './ConfirmModal';

const CortesPdfList = ({ onSelectPdf }) => {
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [detalhes, setDetalhes] = useState(null);
  const [loadingDetalhes, setLoadingDetalhes] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  useEffect(() => {
    loadPdfs();
  }, []);

  const loadPdfs = async () => {
    try {
      setLoading(true);
      const response = await cortesPdfService.listPdfProcessados();
      setPdfs(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar PDFs processados');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetalhes = async (pdfId) => {
    try {
      setLoadingDetalhes(true);
      const response = await cortesPdfService.getPdfDetalhes(pdfId);
      setDetalhes(response.data);
      setSelectedPdf(pdfId);
      
      if (onSelectPdf) {
        onSelectPdf(response.data);
      }
    } catch (error) {
      toast.error('Erro ao carregar detalhes do PDF');
      console.error(error);
    } finally {
      setLoadingDetalhes(false);
    }
  };

  const handleDelete = async (pdfId) => {
    setPendingDeleteId(pdfId);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    const pdfId = pendingDeleteId;
    setShowConfirmModal(false);
    setPendingDeleteId(null);

    if (!pdfId) return;

    try {
      await cortesPdfService.deletePdfProcessado(pdfId);
      toast.success('PDF deletado com sucesso');
      loadPdfs();

      if (selectedPdf === pdfId) {
        setSelectedPdf(null);
        setDetalhes(null);
      }
    } catch (error) {
      toast.error('Erro ao deletar PDF');
      console.error(error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4">📋 PDFs Processados</h3>
        
        {pdfs.length === 0 ? (
          <p className="text-gray-500 text-center py-8">Nenhum PDF processado ainda</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Arquivo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cortes</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pdfs.map((pdf) => (
                  <tr key={pdf.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{pdf.pdf_filename}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatDate(pdf.data_importacao)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{pdf.total_cortes || pdf.total_detalhes || 0}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                        {pdf.status || 'processado'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm space-x-2">
                      <button
                        onClick={() => handleViewDetalhes(pdf.id)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Ver
                      </button>
                      <button
                        onClick={() => handleDelete(pdf.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Deletar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {loadingDetalhes && (
        <div className="bg-white rounded-lg shadow p-6">
          <LoadingSpinner />
        </div>
      )}

      {detalhes && !loadingDetalhes && (
        <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
          {/* Cabeçalho */}
          <div className="flex justify-between items-start border-b pb-4">
            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                📊 Detalhes: {detalhes.pdf_filename}
              </h3>
              <p className="text-sm text-gray-500">
                Importado em {formatDate(detalhes.data_importacao)}
                {detalhes.created_by_name && ` por ${detalhes.created_by_name}`}
              </p>
            </div>
            <button
              onClick={() => {
                setDetalhes(null);
                setSelectedPdf(null);
              }}
              className="text-gray-500 hover:text-gray-700 text-xl font-bold"
            >
              ×
            </button>
          </div>

          {/* Informações do Cabeçalho do PDF */}
          {detalhes.dados_extras && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-lg font-bold text-gray-800 mb-3">📋 Informações do Pedido</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {detalhes.dados_extras.codigo_pedido && (
                  <div>
                    <span className="text-sm font-medium text-gray-600">Código do Pedido:</span>
                    <p className="text-lg font-bold text-gray-800">{detalhes.dados_extras.codigo_pedido}</p>
                  </div>
                )}
                {detalhes.dados_extras.descricao_pedido && (
                  <div className="md:col-span-2">
                    <span className="text-sm font-medium text-gray-600">Descrição:</span>
                    <p className="text-lg font-bold text-gray-800">{detalhes.dados_extras.descricao_pedido}</p>
                  </div>
                )}
                {detalhes.dados_extras.material && (
                  <div>
                    <span className="text-sm font-medium text-gray-600">Material:</span>
                    <p className="text-lg font-bold text-gray-800">{detalhes.dados_extras.material}</p>
                  </div>
                )}
                {detalhes.dados_extras.espessura_mm && (
                  <div>
                    <span className="text-sm font-medium text-gray-600">Espessura:</span>
                    <p className="text-lg font-bold text-gray-800">{detalhes.dados_extras.espessura_mm} mm</p>
                  </div>
                )}
                {detalhes.dados_extras.cor && (
                  <div>
                    <span className="text-sm font-medium text-gray-600">Cor:</span>
                    <p className="text-lg font-bold text-gray-800">{detalhes.dados_extras.cor}</p>
                  </div>
                )}
                {detalhes.dados_extras.quantidade_pecas && (
                  <div>
                    <span className="text-sm font-medium text-gray-600">Quantidade de Peças:</span>
                    <p className="text-lg font-bold text-gray-800">{detalhes.dados_extras.quantidade_pecas}</p>
                  </div>
                )}
                {detalhes.dados_extras.quantidade_chapas && (
                  <div>
                    <span className="text-sm font-medium text-gray-600">Quantidade de Chapas:</span>
                    <p className="text-lg font-bold text-gray-800">{detalhes.dados_extras.quantidade_chapas}</p>
                  </div>
                )}
                {detalhes.dados_extras.aproveitamento && (
                  <div>
                    <span className="text-sm font-medium text-gray-600">Aproveitamento:</span>
                    <p className="text-lg font-bold text-gray-800">{detalhes.dados_extras.aproveitamento}%</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Informações dos Planos */}
          {detalhes.dados_extras?.planos && detalhes.dados_extras.planos.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="text-lg font-bold text-gray-800 mb-4">📐 Planos de Corte</h4>
              
              {/* Tabela de Planos - Formato similar ao PDF */}
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg border border-green-300">
                  <thead className="bg-green-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase border-b border-green-300"># Plano</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase border-b border-green-300">Qtd</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase border-b border-green-300">⬌chp (Largura)</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase border-b border-green-300">⬍chp (Altura)</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase border-b border-green-300">Peças</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase border-b border-green-300">Sobras</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase border-b border-green-300">Cortes</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase border-b border-green-300">Aprov. (%)</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase border-b border-green-300">Fases</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-green-200">
                    {detalhes.dados_extras.planos.map((plano, idx) => (
                      <tr key={idx} className="hover:bg-green-50">
                        <td className="px-4 py-3 text-sm font-bold text-gray-800">
                          {plano.numero}/{plano.total || '?'}
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-700">
                          {plano.qtd !== undefined ? plano.qtd : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-700">
                          {plano.chp_largura ? `${plano.chp_largura} mm` : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-700">
                          {plano.chp_altura ? `${plano.chp_altura} mm` : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-700 font-semibold">
                          {plano.pecas !== undefined ? plano.pecas : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-700">
                          {plano.sobras !== undefined ? plano.sobras : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-700">
                          {plano.cortes !== undefined ? plano.cortes : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          {plano.aproveitamento !== undefined ? (
                            <span className="font-bold text-green-600">{plano.aproveitamento.toFixed(2)}%</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-700">
                          {plano.fases !== undefined ? plano.fases : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Resumo */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-600">Total de cortes extraídos:</span>
              <span className="text-2xl font-bold text-gray-800">{detalhes.cortes?.length || 0}</span>
            </div>
          </div>

          {/* Tabela de Peças - Organizada por Plano */}
          {detalhes.cortes && detalhes.cortes.length > 0 && (
            <div>
              <h4 className="text-lg font-bold text-gray-800 mb-3">🔨 Peças Extraídas</h4>
              
              {/* Agrupar peças por plano */}
              {(() => {
                const pecasPorPlano = {};
                detalhes.cortes.forEach((corte) => {
                  const planoInfo = corte.observacoes || 'Sem plano';
                  if (!pecasPorPlano[planoInfo]) {
                    pecasPorPlano[planoInfo] = [];
                  }
                  pecasPorPlano[planoInfo].push(corte);
                });

                return Object.entries(pecasPorPlano).map(([plano, pecas]) => (
                  <div key={plano} className="mb-6">
                    <h5 className="text-md font-semibold text-gray-700 mb-2 bg-gray-100 px-3 py-2 rounded">
                      {plano}
                    </h5>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Seq</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qtd</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comprimento</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Largura</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Espessura</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Material</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cor</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {pecas.map((corte) => {
                            const dadosExtras = typeof corte.dados_extras === 'string' 
                              ? JSON.parse(corte.dados_extras || '{}') 
                              : corte.dados_extras || {};
                            
                            return (
                              <tr key={corte.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-900">{corte.sequencia}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">
                                  {dadosExtras.codigo_peca || '-'}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                                  {corte.descricao || '-'}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded font-semibold">
                                    {corte.quantidade || 1}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {corte.comprimento_cm ? (
                                    <span className="font-semibold">{corte.comprimento_cm} cm</span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {corte.largura_cm ? (
                                    <span className="font-semibold">{corte.largura_cm} cm</span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {corte.espessura_mm ? (
                                    <span className="font-semibold">{corte.espessura_mm} mm</span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {corte.material ? (
                                    <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                                      {corte.material}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {corte.cor ? (
                                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs">
                                      {corte.cor}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      )}

      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setPendingDeleteId(null);
        }}
        onConfirm={confirmDelete}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja deletar este PDF e todos os cortes associados?"
      />
    </div>
  );
};

export default CortesPdfList;
